<template>
    <v-overlay :value="value" :z-index="1300" opacity="0.48" class="route-publication-overlay">
        <v-card
            v-if="value"
            class="route-publication-wizard"
            light
            role="dialog"
            aria-modal="true"
            :aria-label="title"
        >
            <header class="route-publication-wizard__header">
                <div>
                    <div class="text-overline grey--text text--darken-1">{{ eyebrow }}</div>
                    <h2 class="text-h6 mb-0">{{ title }}</h2>
                </div>
                <v-chip small outlined color="primary">
                    {{ currentPosition }} / {{ steps.length }}
                </v-chip>
            </header>

            <div class="route-publication-wizard__layout">
                <nav class="route-publication-wizard__timeline" :aria-label="timelineLabel">
                    <ol>
                        <li
                            v-for="(step, index) in steps"
                            :key="step.key"
                            :class="[
                                'route-publication-wizard__step',
                                `route-publication-wizard__step--${step.status || 'upcoming'}`,
                            ]"
                            :aria-current="step.key === currentStep ? 'step' : null"
                        >
                            <span class="route-publication-wizard__step-marker">
                                <v-icon v-if="step.status === 'complete'" x-small color="white">mdi-check</v-icon>
                                <v-icon v-else-if="step.status === 'skipped'" x-small color="grey">mdi-minus</v-icon>
                                <span v-else>{{ index + 1 }}</span>
                            </span>
                            <span class="route-publication-wizard__step-copy">
                                <strong>{{ step.label }}</strong>
                                <small v-if="step.caption">{{ step.caption }}</small>
                            </span>
                        </li>
                    </ol>
                </nav>

                <main class="route-publication-wizard__content">
                    <slot />
                    <transition name="route-publication-message" mode="out-in">
                        <v-alert
                            v-if="errorMessage"
                            key="error"
                            type="error"
                            dense
                            dark
                            dismissible
                            close-label="Fechar mensagem"
                            role="alert"
                            class="route-publication-wizard__message mt-4"
                            @input="$emit('dismiss-message', 'error')"
                        >
                            {{ errorMessage }}
                        </v-alert>
                        <v-alert
                            v-else-if="successMessage"
                            key="success"
                            type="success"
                            dense
                            dark
                            dismissible
                            close-label="Fechar mensagem"
                            role="status"
                            class="route-publication-wizard__message mt-4"
                            @input="$emit('dismiss-message', 'success')"
                        >
                            {{ successMessage }}
                        </v-alert>
                        <v-alert
                            v-else-if="noticeMessage"
                            key="notice"
                            type="info"
                            dense
                            dark
                            dismissible
                            close-label="Fechar mensagem"
                            role="status"
                            class="route-publication-wizard__message mt-4"
                            @input="$emit('dismiss-message', 'notice')"
                        >
                            {{ noticeMessage }}
                        </v-alert>
                    </transition>
                </main>
            </div>

            <footer class="route-publication-wizard__footer">
                <v-btn
                    v-if="canCancel"
                    text
                    color="grey darken-2"
                    :disabled="busy"
                    @click="$emit('cancel')"
                >
                    {{ cancelLabel }}
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="canBack"
                    text
                    color="primary"
                    :disabled="busy"
                    @click="$emit('back')"
                >
                    <v-icon left>mdi-chevron-left</v-icon>
                    {{ backLabel }}
                </v-btn>
                <v-btn
                    color="primary"
                    dark
                    :loading="busy"
                    :disabled="busy || !canAdvance"
                    @click="$emit('advance')"
                >
                    {{ advanceLabel }}
                    <v-icon right>mdi-chevron-right</v-icon>
                </v-btn>
            </footer>
        </v-card>
    </v-overlay>
</template>

<script>
export default {
    name: 'RoteirizacaoPublicacaoWizard',
    props: {
        value: { type: Boolean, default: false },
        title: { type: String, required: true },
        eyebrow: { type: String, default: '' },
        timelineLabel: { type: String, default: '' },
        steps: { type: Array, default: () => [] },
        currentStep: { type: String, default: '' },
        busy: { type: Boolean, default: false },
        canBack: { type: Boolean, default: false },
        canCancel: { type: Boolean, default: false },
        canAdvance: { type: Boolean, default: true },
        backLabel: { type: String, default: 'Voltar' },
        cancelLabel: { type: String, default: 'Cancelar' },
        advanceLabel: { type: String, default: 'Avançar' },
        errorMessage: { type: String, default: '' },
        successMessage: { type: String, default: '' },
        noticeMessage: { type: String, default: '' },
    },
    computed: {
        currentPosition() {
            const index = this.steps.findIndex((step) => step.key === this.currentStep);
            return index >= 0 ? index + 1 : 1;
        },
    },
};
</script>

<style scoped>
.route-publication-wizard {
    display: flex;
    flex-direction: column;
    width: min(1280px, calc(100vw - 48px));
    height: min(760px, calc(100vh - 48px));
    overflow: hidden;
}
.route-publication-wizard__header,
.route-publication-wizard__footer {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    gap: 12px;
    padding: 12px 24px;
    background: #fff;
}
.route-publication-wizard__header { border-bottom: 1px solid rgba(0, 0, 0, 0.12); justify-content: space-between; }
.route-publication-wizard__footer { border-top: 1px solid rgba(0, 0, 0, 0.12); }
.route-publication-wizard__layout { display: grid; grid-template-columns: 250px minmax(0, 1fr); flex: 1 1 auto; min-height: 0; }
.route-publication-wizard__timeline { overflow-y: auto; padding: 24px 18px; border-right: 1px solid rgba(0, 0, 0, 0.12); background: #fafafa; }
.route-publication-wizard__timeline ol { margin: 0; padding: 0; list-style: none; }
.route-publication-wizard__step { position: relative; display: flex; gap: 12px; min-height: 64px; color: rgba(0, 0, 0, 0.54); }
.route-publication-wizard__step:not(:last-child)::after { position: absolute; top: 28px; bottom: 0; left: 13px; width: 2px; content: ''; background: rgba(0, 0, 0, 0.12); }
.route-publication-wizard__step-marker { position: relative; z-index: 1; display: inline-flex; align-items: center; justify-content: center; flex: 0 0 28px; width: 28px; height: 28px; border: 2px solid #bdbdbd; border-radius: 50%; background: #fff; font-size: 0.75rem; font-weight: 700; }
.route-publication-wizard__step-copy { display: flex; flex-direction: column; padding-top: 3px; line-height: 1.25; }
.route-publication-wizard__step-copy small { margin-top: 3px; font-size: 0.7rem; font-weight: 400; }
.route-publication-wizard__step--active { color: #1976d2; }
.route-publication-wizard__step--active .route-publication-wizard__step-marker { border-color: #1976d2; color: #1976d2; }
.route-publication-wizard__step--complete { color: rgba(0, 0, 0, 0.78); }
.route-publication-wizard__step--complete .route-publication-wizard__step-marker { border-color: #1976d2; background: #1976d2; }
.route-publication-wizard__step--skipped { color: rgba(0, 0, 0, 0.38); }
.route-publication-wizard__content { min-width: 0; overflow-y: auto; padding: 16px; background: #fff; }
.route-publication-wizard__message {
    width: min(680px, 100%);
    margin-right: auto;
    margin-left: auto;
    border-radius: 8px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
    font-weight: 500;
}
.route-publication-message-enter-active,
.route-publication-message-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.route-publication-message-enter,
.route-publication-message-leave-to { opacity: 0; transform: translateY(-8px); }
@media (max-width: 760px) {
    .route-publication-wizard { width: calc(100vw - 16px); height: calc(100vh - 16px); }
    .route-publication-wizard__header, .route-publication-wizard__footer { padding: 12px 16px; }
    .route-publication-wizard__layout { grid-template-columns: 1fr; grid-template-rows: auto minmax(0, 1fr); }
    .route-publication-wizard__timeline { overflow-x: auto; overflow-y: hidden; padding: 12px 16px; border-right: 0; border-bottom: 1px solid rgba(0, 0, 0, 0.12); }
    .route-publication-wizard__timeline ol { display: flex; min-width: max-content; }
    .route-publication-wizard__step { min-width: 142px; min-height: 40px; }
    .route-publication-wizard__step:not(:last-child)::after { top: 13px; right: 0; bottom: auto; left: 28px; width: auto; height: 2px; }
    .route-publication-wizard__step-copy small { display: none; }
    .route-publication-wizard__content { padding: 16px; }
}
</style>
